import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Search } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { formatRupiah } from '@/lib/utils'
import Skeleton from '@/components/Skeleton'
import { useOrg } from '@/context/OrgContext'

interface Product {
  id: string
  nama: string
  harga: number
  harga_sewa: number | null
  stok: number
  gambar_url?: string
  satuan: string
}

export default function Items() {
  const navigate = useNavigate()
  const { orgId, isRental, loading: orgLoading } = useOrg()
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (orgLoading) return
    fetchProducts()
  }, [orgId, orgLoading])

  async function fetchProducts() {
    try {
      let q = supabase
        .from('produk')
        .select('id, harga, harga_sewa, stok, gambar_url, aktif, satuan_id, master_item_id')
        .order('created_at', { ascending: false })

      if (orgId) q = q.eq('organization_id', orgId)

      const { data, error } = await q

      if (error) throw error

      const rows = data || []
      if (rows.length === 0) { setProducts([]); return }

      const masterIds = [...new Set(rows.map((p: any) => p.master_item_id).filter(Boolean))]
      const satuanIds = [...new Set(rows.map((p: any) => p.satuan_id).filter(Boolean))]

      const [{ data: masters }, { data: satuans }] = await Promise.all([
        supabase.from('master_item').select('id, nama_item').in('id', masterIds),
        supabase.from('satuan').select('id, nama').in('id', satuanIds),
      ])

      const masterMap: Record<string, string> = {}
      for (const m of masters ?? []) masterMap[m.id] = m.nama_item
      const satuanMap: Record<string, string> = {}
      for (const s of satuans ?? []) satuanMap[s.id] = s.nama

      const normalized = rows.map((p: any) => ({
        ...p,
        nama: masterMap[p.master_item_id] || '',
        satuan: satuanMap[p.satuan_id] || 'galon',
      }))
      setProducts(normalized)
    } catch (error) {
      console.error('Error fetching products:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredProducts = products.filter(p =>
    p.nama.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-12">
      <div className="flex items-center mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft size={16} />
          Kembali
        </button>
        <h1 className="text-base font-bold text-gray-900 absolute left-1/2 -translate-x-1/2">
          Pilih Produk
        </h1>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Cari produk..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>Tidak ada produk ditemukan</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {filteredProducts.map(product => (
            <button
              key={product.id}
              onClick={() => navigate(`/produk/${product.id}`)}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all overflow-hidden text-left"
            >
              <div className="w-full h-32 bg-blue-50 flex items-center justify-center text-4xl overflow-hidden">
                {product.gambar_url ? (
                  <img
                    src={product.gambar_url}
                    alt={product.nama}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  '💧'
                )}
              </div>

              <div className="p-3">
                <p className="font-medium text-gray-900 text-sm line-clamp-2">
                  {product.nama}
                </p>
                <p className="text-blue-600 font-semibold text-xs mt-1">
                  {formatRupiah(isRental ? (product.harga_sewa ?? product.harga) : product.harga)}
                  {isRental && <span className="text-gray-400 font-normal"> /hari</span>}
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {product.stok > 0 ? `Stok: ${product.stok}` : 'Stok Habis'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
